import { useEffect, useMemo, useState } from "react";
import api from "../utils/api";

export interface ProgressTotals {
  total: number;
  installed: number;
  uninstalled: number;
  occluded: number;
  real_vs_plan_dev: number;
}

export interface ProgressByType {
  [type: string]: {
    total: number;
    installed: number;
    uninstalled: number;
    occluded: number;
    real_vs_plan_dev: number;
  };
}

export interface ProgressRow {
  type: string;
  pct: number;
  total: number;
  actual: number;
  remaining: number;
}

export interface ProgressData {
  totals: ProgressTotals;
  rows: ProgressRow[];
  overallPct: number;
  remaining: number;
  actualPct: number;
  remainingPct: number;
  devPct: number;
  isLoading: boolean;
}

export const useProgressData = (
  projectId?: string,
  sectionId?: string
): ProgressData => {
  const [objects, setObjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      if (projectId && sectionId) {
        setIsLoading(true);
        try {
          const response = await api.fetchDetectedObjectsBySection(projectId, sectionId);
          setObjects(response?.data || []);
        } catch (_) {
          setObjects([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setObjects([]);
        setIsLoading(false);
      }
    };
    load();
  }, [projectId, sectionId]);

  const source = objects;

  const { totals, byType } = useMemo(() => {
    const totals: ProgressTotals = {
      total: source.length,
      installed: 0,
      uninstalled: 0,
      occluded: 0,
      real_vs_plan_dev: 0,
    };

    const byType: ProgressByType = {};

    for (const detectedObject of source) {
      (totals as any)[detectedObject.status] = ((totals as any)[detectedObject.status] || 0) + 1;

      if (!byType[detectedObject.type]) {
        byType[detectedObject.type] = {
          total: 0,
          installed: 0,
          uninstalled: 0,
          occluded: 0,
          real_vs_plan_dev: 0,
        };
      }
      const objectTypeData = byType[detectedObject.type];
      objectTypeData.total += 1;
      (objectTypeData as any)[detectedObject.status] = ((objectTypeData as any)[detectedObject.status] || 0) + 1;
    }

    return { totals, byType };
  }, [source]);

  const remaining = totals.total - totals.installed;
  const overallPct = totals.total ? Math.round((totals.installed / totals.total) * 100) : 0;

  const rows = useMemo(() => {
    return Object.entries(byType)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([type, v]) => {
        const pct = v.total ? Math.round((v.installed / v.total) * 100) : 0;
        const rem = v.total - v.installed;
        return {
          type,
          pct,
          total: v.total,
          actual: v.installed,
          remaining: rem,
        };
      });
  }, [byType]);

  const actualPct = totals.total ? (totals.installed / totals.total) * 100 : 0;
  const remainingPct = totals.total ? ((totals.total - totals.installed) / totals.total) * 100 : 0;
  const devPct = totals.total ? (totals.real_vs_plan_dev / totals.total) * 100 : 0;

  return {
    totals,
    rows,
    overallPct,
    remaining,
    actualPct,
    remainingPct,
    devPct,
    isLoading,
  };
};
